import { useMemo, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useCameraList } from './use-camera-data';
import { mockApi } from '@/core/services/api';
import { useCameraStore } from '@smolt/shared/store';
import type { Camera } from '@smolt/shared/types';

interface AggregatedCameraState {
  id: string;
  name: string;
  cameraState: string;
  sessionState: string;
}

interface UseAggregatedCameraStatesReturn {
  cameraStates: AggregatedCameraState[];
  cameras: Camera[];
  isLoading: boolean;
}

/**
 * Aggregated camera states hook
 *
 * Fetches individual cameras and aggregates their states for parent component use.
 * Designed to prevent parent rerenders by only computing state changes (not count/weight/duration).
 *
 * Uses useQueries for per-camera subscriptions with intelligent polling:
 * - Counting cameras poll every 250ms
 * - Ready cameras poll every 5s
 *
 * Returns:
 * - cameraStates: Minimal state for action buttons (excludes count/weight/duration)
 * - cameras: Full camera objects for modals
 * - isLoading: True if any camera query is loading
 */
export const useAggregatedCameraStates = (): UseAggregatedCameraStatesReturn => {
  // Get lightweight camera list (IDs only, polls every 5s)
  const { data: cameraList } = useCameraList();

  // Fetch each camera individually with proper isolation
  const queries = useQueries({
    queries: (cameraList || []).map(({ id }) => ({
      queryKey: ['camera', id],
      queryFn: () => mockApi.getCameraById(id),
      enabled: !!id,
      structuralSharing: true,
      notifyOnChangeProps: ['data', 'error'] as const,
      refetchInterval: (query: { state: { data?: Camera | null } }) => {
        const isCounting = query.state.data?.sessionState === 'counting';
        return isCounting ? 250 : 5000;
      },
    })),
  });

  // Extract dependency values for proper static analysis
  const cameraIds = queries.map((q) => q.data?.id).join(',');
  const cameraStatesStr = queries.map((q) => q.data?.cameraState).join(',');
  const sessionStatesStr = queries.map((q) => q.data?.sessionState).join(',');

  // Aggregate camera states (ONLY state fields, not count/weight/duration)
  // This memoization depends on state strings, so it won't recompute on count updates
  const cameraStates = useMemo(() => {
    return queries
      .filter((q) => q.isSuccess && q.data)
      .map((q) => ({
        id: q.data!.id,
        name: q.data!.name,
        cameraState: q.data!.cameraState,
        sessionState: q.data!.sessionState,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraIds, cameraStatesStr, sessionStatesStr]);

  // Full cameras array for modal compatibility
  const cameras = useMemo(() => {
    return queries.filter((q) => q.isSuccess && q.data).map((q) => q.data!);
  }, [queries]);

  // Sync cameras to Zustand store for SessionDisplays, canStartAllSessions etc.
  const setCameras = useCameraStore((state) => state.setCameras);
  useEffect(() => {
    if (cameras.length > 0) {
      setCameras(cameras);
    }
  }, [cameras, setCameras]);

  // Loading state
  const isLoading = queries.some((q) => q.isLoading);

  return {
    cameraStates,
    cameras,
    isLoading,
  };
};
